class WeeklyMenuSelector extends HTMLElement {
  constructor() {
    super();
    
    this.currentProduct = null;
    this.productData = null;
    this.selectedWeeklyMenu = null;
    this.selectedDeliveryDate = null;
    this.pendingFormData = null;
    this.pendingForm = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Weekly menu dropdown change
    this.addEventListener('change', (event) => {
      if (event.target.matches('#weekly-menu-selector-select')) {
        this.onWeeklyMenuChange(event);
      }
    });

    // Delivery date button clicks
    this.addEventListener('click', (event) => {
      const button = event.target.closest('.weekly-menu-selector__delivery-date-button');
      if (button) {
        this.selectDeliveryDate(button);
      }
    });

    // Continue button click
    this.addEventListener('click', (event) => {
      if (event.target.closest('.weekly-menu-selector__continue-button')) {
        this.onContinueButtonClick();
      }
    });

    // Clear cart & continue button click
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-clear-button]')) {
        this.onClearCartButtonClick();
      }
    });

    // Overlay click to close
    this.addEventListener('click', (event) => {
      if (event.target.matches('.weekly-menu-selector__overlay')) {
        this.close();
      }
    });

    // Close button click
    this.addEventListener('click', (event) => {
      if (event.target.closest('.weekly-menu-selector__close-button')) {
        this.close();
      }
    });

    // Escape key to close
    document.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape' && this.classList.contains('active')) {
        this.close();
      }
    });
  }

  async validateAndProceed(productHandle, formData, form) {
    try {
      // Get product data
      const productDataElement = document.getElementById(`product-data-${productHandle}`);
      if (!productDataElement) {
        console.error('Product data not found for handle:', productHandle);
        return false;
      }

      this.productData = JSON.parse(productDataElement.textContent);
      this.pendingFormData = formData;
      this.pendingForm = form;

      // Use shared validation utility
      const validation = await WeeklyMenuUtils.validateWeeklyMenuCompatibility(this.productData);

      if (validation.canProceed) {
        return true;
      }

      if (validation.action === 'selectMenu') {
        // Check if we're on a collection page that corresponds to a weekly menu
        const collectionMenu = this.detectCollectionWeeklyMenu(validation.availableMenus);

        if (collectionMenu) {
          this.showDeliveryDateSelection(collectionMenu, validation.availableMenus);
        } else {
          this.showMenuSelection(validation.availableMenus);
        }
      } else if (validation.action === 'resolveConflict') {
        this.showMenuConflict(validation);
      } else if (validation.action === 'noMenusAvailable') {
        this.showNoMenusAvailable();
      }

      return false;

    } catch (error) {
      console.error('Error in validateAndProceed:', error);
      return false;
    }
  }

  getSessionData() {
    return WeeklyMenuUtils.getSessionData();
  }

  setSessionData(data) {
    WeeklyMenuUtils.setSessionData(data);
  }

  clearSessionData() {
    WeeklyMenuUtils.clearSessionData();
  }

  getAvailableWeeklyMenus() {
    return WeeklyMenuUtils.getAvailableWeeklyMenus(this.productData.weeklyMenus);
  }

  detectCollectionWeeklyMenu(availableMenus) {
    // Check if we're on a collection page and if there's a matching weekly menu
    if (!window.pageContext || window.pageContext.type !== 'collection' || !window.pageContext.collection_handle) {
      return null;
    }

    if (!window.weeklyMenus || !Array.isArray(window.weeklyMenus)) {
      return null;
    }

    if (!availableMenus || !Array.isArray(availableMenus) || availableMenus.length === 0) {
      return null;
    }

    const currentCollectionHandle = window.pageContext.collection_handle;

    // Find the global weekly menu that matches this collection
    const globalWeeklyMenu = window.weeklyMenus.find(menu => 
      menu.collection_handle === currentCollectionHandle
    );

    if (!globalWeeklyMenu) {
      return null;
    }

    // Check if this weekly menu is available for the current product
    // Try multiple matching strategies to be more robust
    const matchingMenu = availableMenus.find(menu => {
      return menu.id === globalWeeklyMenu.id || 
             menu.handle === globalWeeklyMenu.handle ||
             menu.title === globalWeeklyMenu.title ||
             (menu.collection_handle && menu.collection_handle === globalWeeklyMenu.collection_handle);
    });

    return matchingMenu || null;
  }

  isDeliveryDateValid(menu, deliveryDateString) {
    return WeeklyMenuUtils.isDeliveryDateValid(menu, deliveryDateString);
  }

  isDeliveryDateAvailable(dateInfo) {
    return WeeklyMenuUtils.isDeliveryDateAvailable(dateInfo);
  }

  showMenuSelection(availableMenus) {
    this.showView('menu-selection');

    this.populateMenuOptions(availableMenus);

    this.resetMenuSelectionForm();

    // Auto-select if only one menu is available
    if (availableMenus.length === 1) {
      this.autoSelectSingleMenu(availableMenus);
    }

    this.open();
  }

  showDeliveryDateSelection(weeklyMenu, availableMenus) {
    this.showView('menu-selection');

    // Pre-select the weekly menu
    this.selectedWeeklyMenu = weeklyMenu;

    // Populate options but hide the dropdown field since we're auto-selecting
    this.populateMenuOptions(availableMenus);

    this.resetMenuSelectionForm();

    const select = this.querySelector('#weekly-menu-selector-select');
    if (select) {
      const menuIndex = availableMenus.findIndex(menu => menu.id === weeklyMenu.id);
      if (menuIndex !== -1) {
        select.value = menuIndex;
        this.selectedWeeklyMenu = availableMenus[menuIndex];
      }
    }

    // Hide the menu selection field since we're auto-selecting
    const fieldContainer = this.querySelector('.weekly-menu-selector__field');
    if (fieldContainer) {
      fieldContainer.style.display = 'none';
    }

    // Show delivery dates immediately
    this.populateDeliveryDates();
    this.updateContinueButton();

    this.open();
  }

  showMenuConflict(validation) {
    this.showView('menu-conflict');

    const currentMenuTitle = this.querySelector('.weekly-menu-selector__current-menu-title');
    if (currentMenuTitle) {
      currentMenuTitle.textContent = validation.currentMenuTitle || '';
    }
    
    this.open();
  }

  showNoMenusAvailable() {
    this.showView('no-menus');
    this.open();
  }

  showView(viewName) {
    this.querySelectorAll('.weekly-menu-selector__view').forEach(view => {
      view.style.display = 'none';
    });
    
    const targetView = this.querySelector(`.weekly-menu-selector__view--${viewName}`);
    if (targetView) {
      targetView.style.display = 'flex';
    }
  }

  populateMenuOptions(availableMenus) {
    const select = this.querySelector('#weekly-menu-selector-select');
    if (!select) return;

    // Clear existing options (except the first placeholder option)
    const options = select.querySelectorAll('option:not(:first-child)');
    options.forEach(option => option.remove());

    // Add new options
    availableMenus.forEach((menu, index) => {
      const option = document.createElement('option');
      option.value = index;
      option.textContent = menu.label || menu.title;
      select.appendChild(option);
    });
  }

  resetMenuSelectionForm() {
    const select = this.querySelector('#weekly-menu-selector-select');
    const fieldContainer = this.querySelector('.weekly-menu-selector__field');
  
    if (select) {
      select.value = '';
    }

    // Reset visibility of form elements
    if (fieldContainer) {
      fieldContainer.style.display = 'block';
    }

    this.hideDeliveryDates();

    this.selectedWeeklyMenu = null;
    this.selectedDeliveryDate = null;

    this.updateContinueButton();
  }

  autoSelectSingleMenu(availableMenus) {
    const select = this.querySelector('#weekly-menu-selector-select');

    if (select) {
      select.value = 0; // First (and only) option
    }

    this.selectedWeeklyMenu = availableMenus[0];
    this.populateDeliveryDates();
    this.updateContinueButton();

    // Hide dropdown field if only one menu is available
    const fieldContainer = this.querySelector('.weekly-menu-selector__field');
    if (fieldContainer) {
      fieldContainer.style.display = availableMenus.length === 1 ? 'none' : 'block';
    }
  }

  onWeeklyMenuChange(event) {
    const selectedIndex = event.target.value;
    
    if (selectedIndex === '') {
      this.selectedWeeklyMenu = null;
      this.hideDeliveryDates();
      this.updateContinueButton();
      return;
    }

    const availableMenus = this.getAvailableWeeklyMenus();
    this.selectedWeeklyMenu = availableMenus[selectedIndex];
    
    this.populateDeliveryDates();
    this.updateContinueButton();
  }

  populateDeliveryDates() {
    const deliveryDatesContainer = this.querySelector('.weekly-menu-selector__delivery-dates');
    const deliveryDatesList = this.querySelector('.weekly-menu-selector__delivery-dates-list');
    if (!deliveryDatesContainer || !deliveryDatesList || !this.selectedWeeklyMenu) {
      return;
    }

    const deliveryDates = this.selectedWeeklyMenu.delivery_dates || [];

    deliveryDatesList.innerHTML = '';
    if (deliveryDates.length === 0) {
      deliveryDatesContainer.style.display = 'none';
      return;
    }

    const now = new Date();

    // Sort delivery dates by date (earliest first)
    const sortedDeliveryDates = deliveryDates
      .map((dateInfo, originalIndex) => {
        if (!dateInfo || !dateInfo.date) {
          return null;
        }

        const deliveryDate = new Date(dateInfo.date + 'T12:00:00');
        if (isNaN(deliveryDate.getTime())) {
          return null;
        }

        const cutoffDate = dateInfo.cutoff_date ? WeeklyMenuUtils.parseESTDate(dateInfo.cutoff_date) : null;

        return {
          ...dateInfo,
          deliveryDate,
          cutoffDate,
          originalIndex
        };
      })
      .filter(Boolean) // Remove null entries
      .sort((a, b) => a.deliveryDate - b.deliveryDate);

    let availableDatesCount = 0;

    // Create delivery date buttons for all dates
    sortedDeliveryDates.forEach((dateInfo, index) => {
      const { deliveryDate, cutoffDate, originalIndex } = dateInfo;

      // Check if delivery date has passed
      const deliveryDateOnly = new Date(deliveryDate.getFullYear(), deliveryDate.getMonth(), deliveryDate.getDate());
      const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const isDeliveryPast = deliveryDateOnly < nowDateOnly;

      // Check if cutoff date has passed
      const isCutoffPast = cutoffDate && cutoffDate < now;

      // Determine if this date should be disabled
      const isDisabled = isDeliveryPast || isCutoffPast;

      if (!isDisabled) {
        availableDatesCount++;
      }

      const button = document.createElement('button');
      button.className = 'weekly-menu-selector__delivery-date-button';
      button.type = 'button';
      button.dataset.index = originalIndex;
      button.dataset.dateInfo = JSON.stringify(dateInfo);
      
      if (isDisabled) {
        button.disabled = true;
        button.title = isCutoffPast 
          ? 'Order cutoff time has passed for this delivery date'
          : 'This delivery date has already passed';
      }
      
      const formattedDate = this.formatDeliveryDate(dateInfo.date);
      button.innerHTML = `
        <span>${formattedDate}</span>${dateInfo.message ? `<span>${dateInfo.message}</span>` : ''}
      `;
      
      deliveryDatesList.appendChild(button);
    });

    if (availableDatesCount === 0) {
      const noValidDatesMessage = document.createElement('p');
      noValidDatesMessage.className = 'weekly-menu-selector__description';
      noValidDatesMessage.textContent = 'No upcoming delivery dates available for this menu.';
      deliveryDatesList.appendChild(noValidDatesMessage);
    }

    deliveryDatesContainer.style.display = 'block';
  }

  hideDeliveryDates() {
    const deliveryDatesContainer = this.querySelector('.weekly-menu-selector__delivery-dates');
    if (deliveryDatesContainer) {
      deliveryDatesContainer.style.display = 'none';
    }
    this.selectedDeliveryDate = null;
  }

  selectDeliveryDate(button) {
    // Don't allow selection of disabled dates
    if (button.disabled) {
      return;
    }

    this.querySelectorAll('.weekly-menu-selector__delivery-date-button').forEach(btn => {
      btn.classList.remove('selected');
    });

    button.classList.add('selected');

    try {
      this.selectedDeliveryDate = JSON.parse(button.dataset.dateInfo);
    } catch (error) {
      console.error('Error parsing delivery date info:', error);
      this.selectedDeliveryDate = null;
    }

    this.updateContinueButton();
  }

  updateContinueButton() {
    const continueButton = this.querySelector('.weekly-menu-selector__continue-button');
    if (!continueButton) return;

    const isEnabled = this.selectedWeeklyMenu && this.selectedDeliveryDate;
    continueButton.disabled = !isEnabled;
  }

  onContinueButtonClick() {
    const conflictView = this.querySelector('.weekly-menu-selector__view--menu-conflict');
    const isConflictViewActive = conflictView && conflictView.style.display === 'flex';
    
    const noMenusView = this.querySelector('.weekly-menu-selector__view--no-menus');
    const isNoMenusViewActive = noMenusView && noMenusView.style.display === 'flex';

    if (isConflictViewActive || isNoMenusViewActive) {
      this.close();
      return;
    }

    if (!this.selectedWeeklyMenu || !this.selectedDeliveryDate) {
      return;
    }

    // Mark that we went through the selection process
    this.classList.add('went-through-selection');

    // Use the enriched session data method to store all required information
    WeeklyMenuUtils.setEnrichedSessionData(this.selectedWeeklyMenu, this.selectedDeliveryDate);

    // Notify other components about the selection
    WeeklyMenuUtils.notifyWeeklyMenuChange({
      menu: this.selectedWeeklyMenu,
      deliveryDate: this.selectedDeliveryDate,
      source: 'weekly-menu-selector'
    });

    // Proceed with form submission (will close modal after successful cart addition)
    this.proceedWithAddToCart();
  }

  async onClearCartButtonClick() {
    try {
      const clearCartBtn = this.querySelector('[data-cart-clear-button]');
      clearCartBtn.disabled = true;

      // Mark that we went through the selection process and cleared cart
      this.classList.add('went-through-selection');

      await this.clearCart();

      this.clearSessionData();

      // Show menu selection UI to allow user to choose menu and date
      const availableMenus = this.getAvailableWeeklyMenus();
      if (availableMenus.length > 0) {
        this.showMenuSelection(availableMenus);
      } else {
        // No available menus, proceed directly
        const formToSubmit = this.pendingForm;
        
        if (formToSubmit) {
          // Find the ProductForm element that contains the form
          const productForm = formToSubmit.closest('product-form');
          if (productForm) {
            // Set a flag to bypass WeeklyMenuSelector validation on the next submission
            productForm.dataset.skipWeeklyMenuValidation = 'true';
          }
        }

        this.close();
        
        // Submit the form after a small delay
        if (formToSubmit) {
          setTimeout(() => {
            formToSubmit.requestSubmit();
          }, 150);
        }
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      // Proceed anyway with menu selection if possible
      const availableMenus = this.getAvailableWeeklyMenus();
      if (availableMenus.length > 0) {
        this.showMenuSelection(availableMenus);
      } else {
        // Fallback to direct submission
        const formToSubmit = this.pendingForm;
        
        if (formToSubmit) {
          const productForm = formToSubmit.closest('product-form');
          if (productForm) {
            productForm.dataset.skipWeeklyMenuValidation = 'true';
          }
        }
        
        this.close();
        
        if (formToSubmit) {
          setTimeout(() => {
            formToSubmit.requestSubmit();
          }, 150);
        }
      }
    }

    clearCartBtn.disabled = false;
  }

  async clearCart() {
    return WeeklyMenuUtils.clearCart();
  }

  async hasCartItems() {
    return WeeklyMenuUtils.hasCartItems();
  }

  async proceedWithAddToCart() {
    if (!this.pendingForm) {
      return;
    }

    try {
      // Check if there are items in the cart and clear them before adding the new item
      const hasItems = await WeeklyMenuUtils.hasCartItems();
      if (hasItems) {
        await this.clearCart();
        // Re-set the session data after clearing cart since clearCart() clears session data
        if (this.selectedWeeklyMenu && this.selectedDeliveryDate) {
          WeeklyMenuUtils.setEnrichedSessionData(this.selectedWeeklyMenu, this.selectedDeliveryDate);
        }
      }
    } catch (error) {
      console.error('Error checking/clearing cart before adding item:', error);
      return;
    }

    // Find the ProductForm element that contains the form
    const productForm = this.pendingForm.closest('product-form');
    if (!productForm) {
      console.error('Could not find ProductForm element');
      return;
    }

    // Set a flag to bypass WeeklyMenuSelector validation on the next submission
    productForm.dataset.skipWeeklyMenuValidation = 'true';

    const submitButton = productForm.submitButton || productForm.querySelector('[type="submit"]');

    if (submitButton) {
      // Listen for the loading class to be removed (indicating completion)
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;
            // If loading class was removed and aria-disabled is not present, cart addition was successful
            if (!target.classList.contains('loading') && !target.hasAttribute('aria-disabled')) {
              // Close the modal after a short delay
              setTimeout(() => {
                this.close();
              }, 200);
              observer.disconnect();
            }
          }
        });
      });

      observer.observe(submitButton, { attributes: true, attributeFilter: ['class', 'aria-disabled'] });

      // Fallback timeout to ensure modal closes
      setTimeout(() => {
        observer.disconnect();
        if (this.classList.contains('active')) {
          this.close();
        }
      }, 5000);
    }

    // Trigger a form submission which will go through the normal flow
    this.pendingForm.requestSubmit();
  }

  open() {
    this.classList.add('active');
    document.body.classList.add('overflow-hidden');
    
    // Focus management
    requestAnimationFrame(() => {
      const firstFocusable = this.querySelector('select, button:not([disabled])');
      if (firstFocusable) {
        firstFocusable.focus();
      }
    });
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    
    // Clear pending data
    this.pendingForm = null;
    this.pendingFormData = null;
    this.selectedWeeklyMenu = null;
    this.selectedDeliveryDate = null;

    // Only ensure zip validator is initialized if we actually went through menu selection
    // and potentially cleared/modified the cart
    if (this.classList.contains('went-through-selection')) {
      this.classList.remove('went-through-selection');
      // Small delay to allow cart drawer to open and load content
      setTimeout(() => {
        if (typeof initializeZipValidator === 'function') {
          initializeZipValidator();
        }
      }, 300);
    }
  }

  formatDeliveryDate(dateString) {
    return WeeklyMenuUtils.formatDeliveryDate(dateString);
  }
}

customElements.define('weekly-menu-selector', WeeklyMenuSelector);
